import { AuthNavParams } from '@euler/app/flows/auth/Auth';
import { LoginMethods } from '@euler/app/flows/auth/components/LoginMethods';
import { useAuthTheme, useLogin } from '@euler/app/flows/auth/hooks';
import { Button } from '@euler/components/Button';
import { FormGroup, FormInput } from '@euler/components/form';
import { ClearIcon, EyeIcon, EyeSlashIcon } from '@euler/components/icons';
import { useAppLoading } from '@euler/components/loading/Provider';
import { PredefinedHitSlops } from '@euler/functions/predefinedHitSlops';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import {
  isAppleSignInSupported,
  useAppleSignIn,
} from '@euler/functions/useAppleSignIn';
import { useIsWeixinAvailable } from '@euler/functions/useIsWeixinAvailable';
import {
  WeixinLoginErrorResult,
  useWeixinLogin,
} from '@euler/functions/useWeixinLogin';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import {
  AuthProviderType,
  AuthenticatorResult,
  WeixinAuthProvider,
} from '@euler/model/auth';
import { assertNever, onErrorIgnore, sha256, showAlert } from '@euler/utils';
import { isMobilePhone } from '@euler/utils/validators';
import { useNavigation } from '@react-navigation/core';
import { StackNavigationProp } from '@react-navigation/stack';
import { ReactNode, memo, useCallback, useMemo, useRef, useState } from 'react';
import {
  Platform,
  StyleProp,
  TextInput,
  TouchableOpacity,
  View,
  ViewStyle,
  useWindowDimensions,
} from 'react-native';
import Toast from 'react-native-root-toast';
import { LoginAgreement } from '../components/Agreement';
import { LoginHeader } from '../components/LoginHeader';
import { LoginScreenView } from '../components/LoginScreenView';

type Field = 'mobile' | 'password';

const LoginForm = memo(({ style }: { style?: StyleProp<ViewStyle> }) => {
  const theme = useAuthTheme();
  const { rpx } = useResponsiveContext();
  const loading = useAppLoading();
  const { height } = useWindowDimensions();
  const navigation = useNavigation<StackNavigationProp<AuthNavParams>>();
  const startAppleSignIn = useAppleSignIn();
  const isWeixinAvailable = useIsWeixinAvailable();
  const mobileInputRef = useRef<TextInput>();

  const [mobile, setMobile] = useState(__DEV__ ? '13981987569' : '');
  const [password, setPassword] = useState(__DEV__ ? 'zcXmtNz8e9ks!' : '');
  const [isAgreed, setIsAgreed] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [errors, setErrors] = useState<{ [p in Field]?: string }>({});

  const setFieldError = useCallback(
    (field: Field, error: string | undefined) => {
      setErrors(p => ({ ...p, [field]: error }));
    },
    [],
  );

  const ensureServiceAgreementChecked = useCallback(() => {
    if (!isAgreed) {
      showAlert('提示', '请先同意用户协议和隐私条款，然后继续!').catch(
        onErrorIgnore,
      );
      return false;
    }
    return true;
  }, [isAgreed]);

  const { isLoggingIn, login, handleLoginResult } = useLogin({
    onAppSelection: (apps, authToken) => {
      navigation.push('_AppSelection', { apps, authToken });
    },
  });

  const loginWeixin = useWeixinLogin({
    onSuccess: async (
      nonce: string,
      result: AuthenticatorResult<WeixinAuthProvider>,
    ) => {
      loading.hide();

      if (result.nextAction?.type === 'federation-continuation') {
        // continue with phone
        navigation.push('_LoginPhone', {
          provider: { type: 'weixin', nonce, userInfo: result.extra! },
        });
      } else if (result.nextAction?.type === 'app-selection') {
        navigation.push('_AppSelection', {
          apps: result.nextAction.apps,
          authToken: result.token,
        });
      } else {
        await handleLoginResult(AuthProviderType.Weixin, result);
      }
    },
    onError: (result: WeixinLoginErrorResult) => {
      loading.hide();

      if (result.type === 'weixin_not_installed') {
        Toast.show('微信未安装', {
          duration: 1000,
          position: Toast.positions.BOTTOM,
        });
      } else if (result.type === 'weixin_sdk_error') {
        Toast.show(result.message ?? '微信授权失败', {
          duration: 1500,
          position: Toast.positions.BOTTOM,
        });
      } else {
        void showAlert(result.error.message);
      }
    },
  });

  const onLogin = useCallback(async () => {
    if (!isMobilePhone(mobile)) {
      setFieldError('mobile', '请输入正确的手机号码!');
      return;
    }
    setFieldError('mobile', undefined);

    if (!password?.trim()) {
      setFieldError('password', '请输入登录密码!');
      return;
    }
    setFieldError('password', undefined);

    if (!ensureServiceAgreementChecked()) {
      return;
    }

    const hashedPassword = await sha256(password.trim());

    await login({
      type: AuthProviderType.Password,
      userName: mobile,
      password: hashedPassword,
    });
  }, [mobile, setFieldError, password, ensureServiceAgreementChecked, login]);

  const onLoginPhone = useCallback(() => {
    if (!ensureServiceAgreementChecked()) return;
    navigation.push('_LoginPhone', {});
  }, [navigation, ensureServiceAgreementChecked]);

  const onLoginWechat = useCallback(async () => {
    if (Platform.OS === 'web') {
      showAlert('暂不支持!').catch(onErrorIgnore);
      return;
    }

    if (!ensureServiceAgreementChecked()) return;

    loading.show();

    await loginWeixin();
  }, [ensureServiceAgreementChecked, loading, loginWeixin]);

  const onLoginApple = useCallback(async () => {
    if (!ensureServiceAgreementChecked()) {
      return;
    }

    const result = await startAppleSignIn();
    switch (result.type) {
      case 'canceled':
      case 'unauthorized':
      case 'unavailable':
        break;
      case 'error': {
        result.error && console.error(result.error);
        if ((result.error as any)?.code) {
          return;
        }
        Toast.show(result.message ?? 'Apple登录失败', {
          duration: 1500,
          position: Toast.positions.BOTTOM,
        });
        break;
      }
      case 'authorized': {
        // continue with phone
        navigation.push('_LoginPhone', {
          title: '绑定账号',
          message: '输入手机号接收验证码以绑定账号',
          provider: {
            type: 'appleSignIn',
            authorizationInfo: result.authorizationInfo,
          },
        });
        break;
      }
      case 'authenticated': {
        if (result.result.nextAction?.type === 'app-selection') {
          navigation.push('_AppSelection', {
            apps: result.result.nextAction.apps,
            authToken: result.result.token,
          });
        } else {
          await handleLoginResult(AuthProviderType.AppleSignIn, result.result);
        }
        break;
      }
      default: {
        assertNever(result, 'Unexpected result');
      }
    }
  }, [
    ensureServiceAgreementChecked,
    handleLoginResult,
    navigation,
    startAppleSignIn,
  ]);

  const formGroupStyle = useMemo<ViewStyle>(
    () => ({
      marginBottom: rpx(10),
      paddingTop: rpx(10),
      paddingBottom: rpx(10),
    }),
    [rpx],
  );

  const onClearMobile = useCallback(() => {
    setMobile('');
    mobileInputRef?.current?.focus();
  }, []);

  const onTogglePasswordVisibility = useCallback(() => {
    setShowPassword(x => !x);
  }, []);

  return (
    <View
      css={`
        padding: ${rpx(height <= 540 ? 25 : 50)}px ${rpx(32)}px;
      `}
      style={style}
    >
      <LoginHeader subTitle="登录以继续" qrcodeButton />
      <View
        css={`
          margin-bottom: ${rpx(height <= 540 ? 16 : 32)}px;
        `}
      >
        <FormGroup error={errors['mobile']} style={formGroupStyle}>
          <FormInput
            placeholder="手机号"
            value={mobile}
            onChange={setMobile}
            keyboardType="number-pad"
            bgColor={theme.textInput.bgColor}
            placeholderTextColor={theme.textInput.placeholderTextColor}
            inputRef={mobileInputRef}
            css={`
              color: ${theme.textInput.textColor};
              font-size: ${rpx(14)}px;
              padding: 0 ${rpx(44)}px 0 ${rpx(22)}px;
              height: ${rpx(44)}px;
              border-radius: ${rpx(44) / 2}px;
            `}
          />

          {mobile && (
            <InputRight onPress={onClearMobile}>
              <ClearIcon size={rpx(16)} fill="#ccc" />
            </InputRight>
          )}
        </FormGroup>
        <FormGroup error={errors['password']} style={formGroupStyle}>
          <FormInput
            placeholder="密码"
            value={password}
            isPassword={!showPassword}
            onChange={setPassword}
            bgColor={theme.textInput.bgColor}
            placeholderTextColor={theme.textInput.placeholderTextColor}
            css={`
              color: ${theme.textInput.textColor};
              font-size: ${rpx(14)}px;
              padding: 0 ${rpx(22)}px;
              height: ${rpx(44)}px;
              border-radius: ${rpx(44) / 2}px;
            `}
          />
          {password && (
            <InputRight onPress={onTogglePasswordVisibility}>
              {showPassword ? (
                <EyeSlashIcon
                  size={rpx(17)}
                  fill={showPassword ? '#333' : '#ccc'}
                />
              ) : (
                <EyeIcon size={rpx(17)} fill={showPassword ? '#333' : '#ccc'} />
              )}
            </InputRight>
          )}
        </FormGroup>
        <FormGroup style={formGroupStyle}>
          <LoginAgreement checked={isAgreed} onChange={setIsAgreed} />
        </FormGroup>
      </View>
      <Button
        title="登录"
        onPress={onLogin}
        loading={isLoggingIn}
        textStyle={{
          color: theme.buttonTextColor,
          fontSize: rpx(18),
          lineHeight: rpx(32),
        }}
        css={`
          background-color: ${theme.buttonBgColor};
          height: ${rpx(50)}px;
          border-radius: ${rpx(50) / 2}px;
        `}
      />
      <LoginMethods
        enableWechatLogin={isWeixinAvailable}
        enableAppleLogin={isAppleSignInSupported()}
        onLoginPhone={onLoginPhone}
        onLoginWechat={onLoginWechat}
        onLoginApple={onLoginApple}
        css={`
          margin-top: ${rpx(32)}px;
        `}
      />
    </View>
  );
});

export const LoginScreen = wrapNavigatorScreen(() => {
  return (
    <LoginScreenView>
      <LoginForm
        css={`
          align-self: stretch;
        `}
      />
    </LoginScreenView>
  );
});

const InputRight = memo(
  ({
    children,
    style,
    onPress,
  }: {
    children?: ReactNode;
    style?: StyleProp<ViewStyle>;
    onPress?: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    return (
      <TouchableOpacity
        css={`
          top: ${rpx(10)}px;
          bottom: ${rpx(10)}px;
          right: 0;
          width: ${rpx(44)}px;
          position: absolute;
          z-index: 1;
          justify-content: center;
          align-items: center;
        `}
        style={style}
        hitSlop={PredefinedHitSlops.large}
        onPress={onPress}
      >
        {children}
      </TouchableOpacity>
    );
  },
);
